/* Floating "Demo Tools" panel — visible only inside a demo-access session.

   Lets prospects jump straight into the Family Principal and External Adviser
   portal views in a new tab without logging out of the main demo. The panel
   asks the backend for a short-lived portal JWT, then opens
   `/portal/preview?token=...&to=family|adviser`. The preview route stashes
   the token and redirects to the right portal landing page.

   Detection: the DemoAccess page sets sessionStorage flag 'qindex_demo_session'
   after a successful demo-access entry. We read that here.
*/
import { useEffect, useState, useRef } from 'react';
import { Eye, X, ChevronUp, Users, Briefcase, ExternalLink } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const FLAG = 'qindex_demo_session';

const ROLE_LABEL = {
  family_principal: 'Family Principal',
  external_adviser: 'External Adviser',
};
const ROLE_ICON = {
  family_principal: Users,
  external_adviser: Briefcase,
};

export default function DemoToolsPanel() {
  const { token } = useAuth();
  const [inDemo, setInDemo] = useState(false);
  const [open, setOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    try {
      setInDemo(sessionStorage.getItem(FLAG) === '1');
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (!inDemo || !token || fetchedRef.current) return;
    fetchedRef.current = true;
    setLoading(true);
    axios
      .get(`${API}/api/portal/demo-preview/users`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((r) => setUsers(r.data?.users || []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, [inDemo, token]);

  if (!inDemo) return null;

  const previewAs = async (u) => {
    setBusyId(u.id);
    try {
      const r = await axios.post(
        `${API}/api/portal/demo-preview/${u.id}/token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const previewToken = r.data?.token;
      const to = u.portalRole === 'family_principal' ? 'family' : 'adviser';
      if (previewToken) {
        const url = `${window.location.origin}/portal/preview?token=${encodeURIComponent(previewToken)}&to=${to}&name=${encodeURIComponent(u.name || '')}`;
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    } catch {
      /* silent — keep demo polished */
    } finally {
      setBusyId(null);
    }
  };

  // Collapsed pill
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="demo-tools-open"
        className="fixed bottom-24 right-5 z-40 inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#2C2420] text-white text-sm font-medium shadow-xl hover:bg-[#4A3B32] transition-colors"
      >
        <Eye className="w-4 h-4" />
        Demo tools
        <ChevronUp className="w-3.5 h-3.5 opacity-70" />
      </button>
    );
  }

  return (
    <div
      data-testid="demo-tools-panel"
      className="fixed bottom-24 right-5 z-40 w-80 rounded-2xl shadow-2xl border border-[#4A3B32]/30 bg-white overflow-hidden"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-[#2C2420] text-white">
        <div className="flex items-center gap-2">
          <Eye className="w-4 h-4" />
          <p className="text-sm font-medium">Demo tools</p>
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-white/70 hover:text-white"
          data-testid="demo-tools-close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4">
        <p className="text-xs text-[#5D524D] mb-3 leading-relaxed">
          Jump straight into a portal view in a new tab &mdash; no need to log out.
          You stay signed in here.
        </p>

        {loading && (
          <div className="text-sm text-[#8C8078] py-4 text-center">Loading…</div>
        )}

        {!loading && users.length === 0 && (
          <div className="text-xs text-[#8C8078] py-4 leading-relaxed">
            No portal previews available for this demo org. Seed Family Principal
            or External Adviser portal users to enable.
          </div>
        )}

        {!loading && users.length > 0 && (
          <ul className="space-y-2" data-testid="demo-tools-user-list">
            {users.map((u) => {
              const Icon = ROLE_ICON[u.portalRole] || Eye;
              return (
                <li key={u.id}>
                  <button
                    type="button"
                    onClick={() => previewAs(u)}
                    disabled={busyId === u.id}
                    data-testid={`demo-preview-${u.portalRole}-${u.id}`}
                    className={cn(
                      'w-full text-left px-3 py-2.5 rounded-lg border border-[#E6E0D9] hover:border-[#4A3B32]/40 hover:bg-[#F9F7F5] transition-colors',
                      busyId === u.id && 'opacity-60 cursor-wait'
                    )}
                  >
                    <div className="flex items-start gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-[#A86A3A]/10 flex items-center justify-center shrink-0">
                        <Icon className="w-4 h-4 text-[#A86A3A]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#2C2420] truncate">{u.name}</p>
                        <p className="text-xs text-[#8C8078] truncate">
                          {ROLE_LABEL[u.portalRole] || u.portalRole} · {u.email}
                        </p>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-[#8C8078] mt-1 shrink-0" />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <p className="text-[10px] text-[#8C8078] mt-3 leading-relaxed">
          Preview links open in a new tab and expire after 1 hour. Your main demo session is unaffected.
        </p>
      </div>
    </div>
  );
}
